import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import {
  User,
  Users,
  Heart,
  ArrowRight,
  CheckCircle,
  MapPin,
  Star,
  Sparkles,
  UserPlus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const soloOptions = [
  {
    icon: User,
    title: "True Solo Journey",
    description: "Take any itinerary and make it your own personal adventure. You'll have a private guide, flexible schedule, and the freedom to explore at your pace.",
    benefits: ["Complete flexibility", "Personalized attention", "Customize activities", "Your own pace"]
  },
  {
    icon: UserPlus,
    title: "Join a Group Tour Solo",
    description: "Traveling alone doesn't mean being alone. Join one of our scheduled group tours and connect with like-minded travelers from around the world.",
    benefits: ["Meet new people", "Built-in community", "Shared experiences", "Cost-effective"]
  },
  {
    icon: Heart,
    title: "Solo Traveler Matching",
    description: "Want the double-occupancy rate but don't have a travel partner? We can pair you with another solo traveler heading to the same destination around the same time.",
    benefits: ["Save on single supplement", "Find a travel buddy", "Shared accommodation", "New friendships"],
    highlight: true
  }
];

export default function SoloAdventuresPage() {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async () => {
    try {
      const res = await axios.get(`${API}/trips`);
      setTrips(res.data);
    } catch (err) {
      console.error('Failed to fetch trips:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <div className="bg-gradient-to-b from-cyan-50 to-transparent pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-cyan-100 text-cyan-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <User className="h-4 w-4" />
            Solo Doesn't Mean Alone
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-4">
            Solo Adventures
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Every trip in our collection can be your solo adventure. Whether you want complete independence, 
            the energy of a group, or a matched travel companion — we've got you covered.
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Solo Options */}
        <section className="mb-20">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-primary text-center mb-4">
            Your Solo Travel Options
          </h2>
          <p className="text-muted-foreground text-center max-w-2xl mx-auto mb-12">
            Choose the solo experience that fits your travel style
          </p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {soloOptions.map((option, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
              >
                <Card className={`h-full ${option.highlight ? 'border-2 border-secondary ring-2 ring-secondary/20' : ''}`}>
                  {option.highlight && (
                    <div className="bg-secondary text-white text-center py-2 text-sm font-medium">
                      <Sparkles className="h-4 w-4 inline mr-1" />
                      Popular Choice
                    </div>
                  )}
                  <CardHeader className="text-center pb-2">
                    <div className="w-14 h-14 rounded-full bg-cyan-100 flex items-center justify-center mx-auto mb-3">
                      <option.icon className="h-7 w-7 text-cyan-600" />
                    </div>
                    <CardTitle className="text-lg">{option.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-sm text-muted-foreground mb-4">
                      {option.description}
                    </p>
                    <div className="space-y-2">
                      {option.benefits.map((benefit, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                          <span className="text-muted-foreground">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Matching CTA */}
        <section className="mb-20">
          <Card className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white overflow-hidden">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1">
                  <h3 className="font-heading text-2xl md:text-3xl font-semibold mb-4">
                    Solo Traveler Matching Program
                  </h3>
                  <p className="text-white/90 mb-6">
                    Create a free account to access our solo traveler matching feature. See other solo travelers 
                    interested in the same destinations and dates, express interest in being matched, and potentially 
                    find your perfect travel companion.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link to="/register">
                      <Button className="bg-white text-cyan-700 hover:bg-white/90">
                        Create Free Account
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                    <Link to="/login">
                      <Button variant="outline" className="border-white text-white hover:bg-white/10">
                        Sign In
                      </Button>
                    </Link>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-white/20 flex items-center justify-center">
                    <Users className="h-16 w-16 md:h-20 md:w-20 text-white" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* All Trips - Any Can Be Solo */}
        <section>
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-primary mb-4">
              All Itineraries Available for Solo Travelers
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Every trip below can be taken solo, with a friend, or we can attempt to match you with another 
              solo traveler for the double-occupancy rate.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trips.map((trip, idx) => (
                <motion.div
                  key={trip.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.05 }}
                >
                  <Link to={`/trips/${trip.slug}`}>
                    <Card className="overflow-hidden group hover:shadow-lg transition-all h-full">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={trip.image_url}
                          alt={trip.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute top-3 left-3 flex gap-2">
                          <Badge className="bg-cyan-500 text-white">
                            <User className="h-3 w-3 mr-1" />
                            Solo Friendly
                          </Badge>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-heading font-semibold text-primary mb-1 group-hover:text-secondary transition-colors">
                          {trip.title}
                        </h3>
                        <div className="flex items-center gap-1 text-sm text-muted-foreground mb-2">
                          <MapPin className="h-3.5 w-3.5" />
                          {trip.destination}
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                            <span className="text-sm font-medium">{trip.rating}</span>
                          </div>
                          <span className="font-heading font-bold text-secondary">
                            ${trip.price.toLocaleString()}
                          </span>
                        </div>
                        <div className="mt-3 pt-3 border-t text-xs text-muted-foreground">
                          {trip.duration} • Solo, Duo, or Group
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </section>

        {/* Customize CTA */}
        <div className="mt-16 text-center">
          <p className="text-muted-foreground mb-4">
            Want a completely custom solo itinerary?
          </p>
          <Link to="/customize">
            <Button variant="outline" className="border-secondary text-secondary hover:bg-secondary/10">
              Create Custom Trip
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
